/**
 * Project detail page - overview, milestones, team, IP, financials, risk, instruments.
 *
 * TODO (Phase 3 v3.0): Add Deal Document Generation panel here once Aaron's
 * legal templates are wired up. The "Generate Deal Document" button is a
 * placeholder for that workflow.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Pencil,
  Trash2,
  Sparkles,
  FileSignature,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import projectService from '../services/projectService';
import matchService from '../services/matchService';
import capitalReadinessService from '../services/capitalReadinessService';
import momentumService from '../services/momentumService';
import MatchCard from '../components/MatchCard';
import CRSTierBadge from '../components/CRSTierBadge';
import MomentumBadge from '../components/MomentumBadge';
import VelocityIndicator from '../components/VelocityIndicator';
import {
  Project,
  RecommendedInstrument,
} from '../types/project';
import { Match } from '../types/match';
import { CapitalReadinessScore } from '../types/capitalReadiness';
import {
  DealMomentum,
  MomentumTimelineResponse,
} from '../types/dealMomentum';
import { Gauge, Flame, RefreshCw } from 'lucide-react';

type TabKey =
  | 'overview'
  | 'milestones'
  | 'team'
  | 'ip'
  | 'financials'
  | 'risk'
  | 'instruments'
  | 'matches'
  | 'readiness'
  | 'momentum';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'milestones', label: 'Milestones' },
  { key: 'team', label: 'Team' },
  { key: 'ip', label: 'IP' },
  { key: 'financials', label: 'Financials' },
  { key: 'risk', label: 'Risk' },
  { key: 'instruments', label: 'Instruments' },
  { key: 'matches', label: 'Matches' },
  { key: 'readiness', label: 'Readiness Score' },
  { key: 'momentum', label: 'Momentum' },
];

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatMoney = (value?: number): string => {
  if (value === undefined || value === null) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

interface FieldRowProps {
  label: string;
  value?: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, value }) => (
  <div className="flex items-start py-2 border-b border-gray-100 last:border-b-0">
    <div className="w-44 flex-shrink-0 text-xs uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="flex-1 text-sm text-gray-900">
      {value === undefined || value === null || value === '' ? (
        <span className="text-gray-400">—</span>
      ) : (
        value
      )}
    </div>
  </div>
);

const scoreColor = (score: number): string => {
  if (score >= 80) return 'bg-green-100 text-green-800';
  if (score >= 60) return 'bg-yellow-100 text-yellow-800';
  if (score >= 40) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [recommendations, setRecommendations] = useState<RecommendedInstrument[]>([]);
  const [recsLoading, setRecsLoading] = useState<boolean>(false);
  const [recsLoaded, setRecsLoaded] = useState<boolean>(false);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchesLoading, setMatchesLoading] = useState<boolean>(false);
  const [matchesLoaded, setMatchesLoaded] = useState<boolean>(false);
  const [generatingMatches, setGeneratingMatches] = useState<boolean>(false);

  // Readiness tab state
  const [crs, setCrs] = useState<CapitalReadinessScore | null>(null);
  const [crsLoading, setCrsLoading] = useState<boolean>(false);
  const [crsLoaded, setCrsLoaded] = useState<boolean>(false);
  const [rescoring, setRescoring] = useState<boolean>(false);

  // Momentum tab state
  const [momentum, setMomentum] = useState<DealMomentum | null>(null);
  const [momentumTl, setMomentumTl] = useState<MomentumTimelineResponse | null>(
    null
  );
  const [momentumLoading, setMomentumLoading] = useState<boolean>(false);
  const [momentumLoaded, setMomentumLoaded] = useState<boolean>(false);
  const [refreshingMomentum, setRefreshingMomentum] = useState<boolean>(false);

  const loadMatches = async (projectId: number) => {
    setMatchesLoading(true);
    const result = await matchService.fetchProjectMatches(projectId);
    if (result.error) {
      toast.error(result.error);
      setMatches([]);
    } else {
      setMatches(result.data || []);
    }
    setMatchesLoading(false);
    setMatchesLoaded(true);
  };

  const handleGenerateProjectMatches = async () => {
    if (!project) return;
    setGeneratingMatches(true);
    const result = await matchService.generateMatches({ project_id: project.id });
    setGeneratingMatches(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success(`Generated ${result.data?.generated ?? 0} match(es)`);
    await loadMatches(project.id);
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setIsLoading(true);
      const result = await projectService.fetchProject(parseInt(id, 10));
      if (result.error) {
        toast.error(result.error);
        setProject(null);
      } else {
        setProject(result.data || null);
      }
      setIsLoading(false);
    };
    load();
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'matches' || matchesLoaded || !id) return;
    loadMatches(parseInt(id, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, matchesLoaded, id]);

  const loadCrs = async (projectId: number) => {
    setCrsLoading(true);
    const res = await capitalReadinessService.fetchLatest({
      project_id: projectId,
    });
    if (res.error) {
      toast.error(res.error);
    } else {
      setCrs(res.data || null);
    }
    setCrsLoading(false);
    setCrsLoaded(true);
  };

  const handleRescore = async () => {
    if (!project) return;
    setRescoring(true);
    if (crs) {
      const res = await capitalReadinessService.rescore(crs.id);
      setRescoring(false);
      if (res.error) {
        toast.error(res.error);
        return;
      }
      if (res.data) {
        setCrs(res.data);
        toast.success('Rescored');
      }
    } else {
      const res = await capitalReadinessService.scoreEntity({
        project_id: project.id,
      });
      setRescoring(false);
      if (res.error) {
        toast.error(res.error);
        return;
      }
      if (res.data) {
        setCrs(res.data);
        toast.success('Score generated');
      }
    }
  };

  const loadMomentum = async (projectId: number) => {
    setMomentumLoading(true);
    const [sigRes, tlRes] = await Promise.all([
      momentumService.fetchSignals({ project_id: projectId }),
      momentumService.fetchTimeline({ project_id: projectId, days: 30 }),
    ]);
    if (sigRes.error) {
      toast.error(sigRes.error);
    } else {
      setMomentum((sigRes.data || [])[0] || null);
    }
    if (tlRes.error) {
      // silent
    } else {
      setMomentumTl(tlRes.data || null);
    }
    setMomentumLoading(false);
    setMomentumLoaded(true);
  };

  const handleRefreshMomentum = async () => {
    if (!project) return;
    setRefreshingMomentum(true);
    const res = await momentumService.refresh({
      entity_kind: 'project',
      entity_id: project.id,
    });
    setRefreshingMomentum(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setMomentum(res.data);
      toast.success('Momentum refreshed');
      const tlRes = await momentumService.fetchTimeline({
        project_id: project.id,
        days: 30,
      });
      if (tlRes.data) setMomentumTl(tlRes.data);
    }
  };

  useEffect(() => {
    if (activeTab === 'readiness' && !crsLoaded && id) {
      loadCrs(parseInt(id, 10));
    }
    if (activeTab === 'momentum' && !momentumLoaded && id) {
      loadMomentum(parseInt(id, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, crsLoaded, momentumLoaded, id]);

  useEffect(() => {
    if (activeTab !== 'instruments' || recsLoaded || !id) return;
    const loadRecs = async () => {
      setRecsLoading(true);
      const result = await projectService.getRecommendedInstruments(parseInt(id, 10));
      if (result.error) {
        toast.error(result.error);
        setRecommendations([]);
      } else {
        setRecommendations(result.data || []);
      }
      setRecsLoading(false);
      setRecsLoaded(true);
    };
    loadRecs();
  }, [activeTab, recsLoaded, id]);

  const handleDelete = async () => {
    if (!project) return;
    if (!window.confirm(`Delete project "${project.project_name}"? This cannot be undone.`)) {
      return;
    }
    setIsDeleting(true);
    const result = await projectService.deleteProject(project.id);
    if (result.error) {
      toast.error(result.error);
      setIsDeleting(false);
    } else {
      toast.success('Project deleted');
      navigate('/projects');
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading project...
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <p className="text-gray-700 mb-4">Project not found.</p>
          <Link to="/projects" className="text-blue-600 hover:underline text-sm">
            Back to projects
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const milestones = project.milestones || [];
  const teamMembers = project.team_members || [];
  const advisoryBoard = project.advisory_board || [];
  const ipAssets = project.ip_assets || [];
  const useOfFunds = project.use_of_funds || [];
  const riskFactors = project.risk_factors || [];
  const recommended = project.recommended_instruments || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link
            to="/projects"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to projects
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              title="Coming soon: generate deal document from selected instrument"
              disabled
            >
              <FileSignature className="w-4 h-4 mr-1.5" />
              Generate deal doc
            </button>
            <Link
              to={`/projects/new?id=${project.id}`}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="w-4 h-4 mr-1.5" />
              Edit
            </Link>
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {/* Title block */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{project.project_name}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {project.is_standalone
                  ? 'Standalone project'
                  : project.startup_id
                  ? `Linked to startup #${project.startup_id}`
                  : 'No startup link'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {project.pipeline_status && (
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {formatLabel(project.pipeline_status)}
                </span>
              )}
              {project.poc_status && (
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">
                  {formatLabel(project.poc_status)}
                </span>
              )}
            </div>
          </div>
          {project.description && (
            <p className="mt-4 text-sm text-gray-700">{project.description}</p>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          {activeTab === 'overview' && (
            <div className="space-y-1">
              <FieldRow label="Project name" value={project.project_name} />
              <FieldRow label="Description" value={project.description} />
              <FieldRow
                label="Standalone"
                value={project.is_standalone ? 'Yes' : 'No'}
              />
              <FieldRow
                label="Startup link"
                value={project.startup_id ? `#${project.startup_id}` : '—'}
              />
              <FieldRow label="Current phase" value={project.current_phase} />
              <FieldRow label="POC status" value={formatLabel(project.poc_status)} />
              <FieldRow label="Pipeline status" value={formatLabel(project.pipeline_status)} />
              <FieldRow label="Start date" value={formatDate(project.start_date)} />
              <FieldRow
                label="Target completion"
                value={formatDate(project.target_completion)}
              />
              <FieldRow label="Created at" value={formatDate(project.created_at)} />
            </div>
          )}

          {activeTab === 'milestones' && (
            <div className="space-y-3">
              {milestones.length === 0 ? (
                <p className="text-sm text-gray-500">No milestones recorded yet.</p>
              ) : (
                milestones.map((m, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900">
                        {m.name || `Milestone ${idx + 1}`}
                      </div>
                      {m.description && (
                        <p className="text-sm text-gray-600 mt-1">{m.description}</p>
                      )}
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                        {m.target_date && <span>Target: {formatDate(m.target_date)}</span>}
                        {m.status && (
                          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                            {formatLabel(m.status)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'team' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Team members</h3>
                {teamMembers.length === 0 ? (
                  <p className="text-sm text-gray-500">No team members on file.</p>
                ) : (
                  <ul className="space-y-2">
                    {teamMembers.map((tm, idx) => (
                      <li
                        key={idx}
                        className="border border-gray-200 rounded-lg p-3 text-sm text-gray-800"
                      >
                        <div className="font-medium">{tm.name || 'Unnamed'}</div>
                        <div className="text-xs text-gray-600">{tm.role || '—'}</div>
                        {tm.linkedin_url && (
                          <a
                            href={tm.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            LinkedIn
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Advisory board</h3>
                {advisoryBoard.length === 0 ? (
                  <p className="text-sm text-gray-500">No advisors on file.</p>
                ) : (
                  <ul className="space-y-2">
                    {advisoryBoard.map((tm, idx) => (
                      <li
                        key={idx}
                        className="border border-gray-200 rounded-lg p-3 text-sm text-gray-800"
                      >
                        <div className="font-medium">{tm.name || 'Unnamed'}</div>
                        <div className="text-xs text-gray-600">{tm.role || '—'}</div>
                        {tm.linkedin_url && (
                          <a
                            href={tm.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            LinkedIn
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {activeTab === 'ip' && (
            <div className="space-y-3">
              {ipAssets.length === 0 ? (
                <p className="text-sm text-gray-500">No IP assets recorded.</p>
              ) : (
                ipAssets.map((ip, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-3 text-sm text-gray-800"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{formatLabel(ip.type) || 'IP Asset'}</div>
                      {ip.status && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {formatLabel(ip.status)}
                        </span>
                      )}
                    </div>
                    {ip.description && (
                      <p className="text-sm text-gray-600 mt-1">{ip.description}</p>
                    )}
                    {ip.filing_number && (
                      <div className="text-xs text-gray-500 mt-1">
                        Filing #: {ip.filing_number}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="space-y-1">
              <FieldRow label="Funding needed" value={formatMoney(project.funding_needed)} />
              <FieldRow
                label="Funding committed"
                value={formatMoney(project.funding_committed)}
              />
              <FieldRow
                label="Revenue to date"
                value={formatMoney(project.revenue_to_date)}
              />
              <FieldRow label="LOI count" value={project.loi_count ?? 0} />
              <FieldRow label="Revenue model" value={project.revenue_model} />
              {project.revenue_projections && (
                <>
                  <FieldRow
                    label="Year 1 revenue"
                    value={formatMoney(project.revenue_projections.year1)}
                  />
                  <FieldRow
                    label="Year 2 revenue"
                    value={formatMoney(project.revenue_projections.year2)}
                  />
                  <FieldRow
                    label="Year 3 revenue"
                    value={formatMoney(project.revenue_projections.year3)}
                  />
                  <FieldRow
                    label="Assumptions"
                    value={project.revenue_projections.assumptions}
                  />
                </>
              )}
              {useOfFunds.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                    Use of funds
                  </h3>
                  <ul className="space-y-2">
                    {useOfFunds.map((uf, idx) => (
                      <li
                        key={idx}
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-2 text-sm"
                      >
                        <span className="text-gray-800">{uf.category || '—'}</span>
                        <span className="text-gray-600">{formatMoney(uf.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'risk' && (
            <div className="space-y-3">
              <FieldRow label="Risk score (0-100)" value={project.risk_score} />
              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Risk factors
                </h3>
                {riskFactors.length === 0 ? (
                  <p className="text-sm text-gray-500">No risk factors recorded.</p>
                ) : (
                  <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
                    {riskFactors.map((r, idx) => (
                      <li key={idx}>{r}</li>
                    ))}
                  </ul>
                )}
              </div>
              <FieldRow label="Traction notes" value={project.traction_notes} />
            </div>
          )}

          {activeTab === 'instruments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Recommended instruments
                  </h3>
                  <p className="text-xs text-gray-500">
                    Ranked by fit to this project's stage, amount, timeline, and dilution preferences.
                  </p>
                </div>
                {project.selected_instrument && (
                  <span className="text-xs text-gray-700">
                    Selected:{' '}
                    <span className="font-medium">
                      {formatLabel(project.selected_instrument)}
                    </span>
                  </span>
                )}
              </div>

              {recommended.length > 0 && (
                <div className="text-xs text-gray-600">
                  Stored recommendations:{' '}
                  {recommended.map((r) => formatLabel(r)).join(', ')}
                </div>
              )}

              {recsLoading ? (
                <div className="flex items-center text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scoring instruments...
                </div>
              ) : recommendations.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No instruments to recommend yet. Make sure the instrument library is seeded.
                </p>
              ) : (
                <div className="space-y-3">
                  {recommendations.map((rec) => (
                    <div
                      key={rec.instrument_type}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-blue-500" />
                            {rec.display_name || formatLabel(rec.instrument_type)}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {rec.dilutive === false ? 'Non-dilutive' : rec.dilutive ? 'Dilutive' : '—'}
                            {rec.complexity && ` • ${formatLabel(rec.complexity)} complexity`}
                            {(rec.typical_amount_min || rec.typical_amount_max) && (
                              <>
                                {' • '}
                                {formatMoney(rec.typical_amount_min)}–{formatMoney(rec.typical_amount_max)}
                              </>
                            )}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${scoreColor(
                            rec.score
                          )}`}
                        >
                          Score {rec.score}
                        </span>
                      </div>
                      {rec.reasoning && rec.reasoning.length > 0 && (
                        <ul className="list-disc list-inside text-xs text-gray-700 mt-3 space-y-0.5">
                          {rec.reasoning.map((r, idx) => (
                            <li key={idx}>{r}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'matches' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Matches</h3>
                  <p className="text-xs text-gray-500">
                    Top scoring investors for this project.
                  </p>
                </div>
                <button
                  onClick={handleGenerateProjectMatches}
                  disabled={generatingMatches}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {generatingMatches ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      Generate matches
                    </>
                  )}
                </button>
              </div>

              {matchesLoading ? (
                <div className="flex items-center text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading matches...
                </div>
              ) : matches.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No matches yet. Click "Generate matches" to score this project
                  against investors.
                </p>
              ) : (
                <div className="space-y-3">
                  {matches.map((m) => (
                    <MatchCard key={m.id} match={m} hideTarget />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'readiness' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center">
                    <Gauge className="w-4 h-4 mr-1.5 text-blue-500" />
                    Capital Readiness Score
                  </h3>
                  <p className="text-xs text-gray-500">
                    7-dimension readiness analysis with gap detection.
                  </p>
                </div>
                <button
                  onClick={handleRescore}
                  disabled={rescoring}
                  className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {rescoring ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                  )}
                  {crs ? 'Rescore' : 'Score now'}
                </button>
              </div>
              {crsLoading ? (
                <div className="flex items-center text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading score...
                </div>
              ) : !crs ? (
                <p className="text-sm text-gray-500">
                  No readiness score yet for this project.
                </p>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-gray-900">
                        {Math.round(crs.overall_score)}
                        <span className="text-base text-gray-500">/100</span>
                      </div>
                      <div className="mt-1">
                        <CRSTierBadge tier={crs.tier} />
                      </div>
                    </div>
                    <Link
                      to={`/capital-readiness/scores/${crs.id}`}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      View detail →
                    </Link>
                  </div>
                  {crs.gaps && crs.gaps.length > 0 && (
                    <div className="text-xs text-gray-600">
                      {crs.gaps.length} gap{crs.gaps.length === 1 ? '' : 's'}{' '}
                      identified
                      {crs.gaps.some((g) => g.severity === 'critical') &&
                        ' (critical)'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'momentum' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center">
                    <Flame className="w-4 h-4 mr-1.5 text-red-500" />
                    Deal Momentum
                  </h3>
                  <p className="text-xs text-gray-500">
                    Current momentum tier and 30-day velocity timeline.
                  </p>
                </div>
                <button
                  onClick={handleRefreshMomentum}
                  disabled={refreshingMomentum}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50 disabled:opacity-50"
                >
                  {refreshingMomentum ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                  )}
                  Refresh
                </button>
              </div>
              {momentumLoading ? (
                <div className="flex items-center text-sm text-gray-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading momentum...
                </div>
              ) : !momentum ? (
                <p className="text-sm text-gray-500">
                  No momentum data yet. Click "Refresh" to compute.
                </p>
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <MomentumBadge tier={momentum.momentum_tier} />
                    <VelocityIndicator
                      velocity={momentum.velocity_score}
                      size="md"
                    />
                    <span className="text-xs text-gray-500">
                      {momentum.views_this_week} views this week ·{' '}
                      {momentum.match_requests} match requests
                    </span>
                    <Link
                      to={`/momentum/project/${project.id}`}
                      className="ml-auto text-blue-600 hover:underline text-sm"
                    >
                      View detail →
                    </Link>
                  </div>
                  {/* Inline timeline sparkline */}
                  {momentumTl && momentumTl.points.length > 0 && (
                    <TimelineSparkline points={momentumTl.points} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const TimelineSparkline: React.FC<{
  points: Array<{ date: string; velocity_score: number }>;
}> = ({ points }) => {
  const w = 600;
  const h = 80;
  const pad = 8;
  if (points.length === 0) return null;
  const maxV = Math.max(1, ...points.map((p) => p.velocity_score));
  const minV = Math.min(0, ...points.map((p) => p.velocity_score));
  const range = maxV - minV || 1;
  const coords = points.map((p, i) => {
    const x =
      points.length === 1
        ? w / 2
        : pad + ((w - 2 * pad) * i) / (points.length - 1);
    const y = pad + (h - 2 * pad) * (1 - (p.velocity_score - minV) / range);
    return { x, y };
  });
  const d = coords
    .map((c, i) => (i === 0 ? `M ${c.x} ${c.y}` : `L ${c.x} ${c.y}`))
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h}>
      <path d={d} fill="none" stroke="#DC2626" strokeWidth={1.5} />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={1.5} fill="#DC2626" />
      ))}
    </svg>
  );
};

export default ProjectDetailPage;
